/** @jsxImportSource @emotion/react */
import React, { useState } from 'react'
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import type { SchedulerViolation } from '../hooks/useSchedulerRequest'

interface ScheduleViolationsProps {
  violations: SchedulerViolation[]
}

const severityConfig = {
  error: {
    label: 'Errors',
    color: colors.ERROR_200,
    bgColor: 'rgba(223, 66, 66, 0.1)',
    borderColor: colors.ERROR_200,
  },
  warning: {
    label: 'Warnings',
    color: colors.WARNING_200,
    bgColor: 'rgba(255, 204, 49, 0.1)',
    borderColor: colors.WARNING_200,
  },
  info: {
    label: 'Info',
    color: colors.INFO_200,
    bgColor: 'rgba(90, 166, 255, 0.1)',
    borderColor: colors.INFO_200,
  },
} as const

const containerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  margin-top: 24px;
`

const headerStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`

const titleStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 16.8px;
  letter-spacing: 1.68px;
  text-transform: uppercase;
  color: ${colors.WHITE};
  margin: 0;
`

const badgeStyles = (color: string) => css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background-color: ${color};
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 12px;
  color: ${colors.WHITE};
`

const groupStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const groupHeaderStyles = (borderColor: string) => css`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 17.4px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${borderColor};
  margin: 0;
  padding: 8px 16px;
  background-color: ${colors.SECONDARY_400};
  border-radius: 8px 8px 0 0;
  border-left: 3px solid ${borderColor};
`

const violationRowStyles = (bgColor: string, borderColor: string) => css`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 16px;
  background-color: ${bgColor};
  border-left: 3px solid ${borderColor};

  &:last-of-type {
    border-radius: 0 0 8px 8px;
  }
`

const violationMessageStyles = css`
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 18.85px;
  color: ${colors.WHITE};
  margin: 0;
`

const violationTypeStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: ${colors.NEUTRAL_70};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  width: 140px;
`

const suggestionStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 17.4px;
  color: ${colors.NEUTRAL_50};
  margin: 4px 0 0;
  font-style: italic;
`

const affectedGamesStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: ${colors.NEUTRAL_70};
  flex-shrink: 0;
`

const ExpandArrow: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    css={css`
      transition: transform 0.2s ease;
      transform: ${expanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
    `}
  >
    <path d='M7 10L12 15L17 10H7Z' fill={colors.WHITE} />
  </svg>
)

const ScheduleViolations: React.FC<ScheduleViolationsProps> = ({
  violations,
}) => {
  const [expanded, setExpanded] = useState(true)

  const grouped = {
    error: violations.filter((v) => v.severity === 'error'),
    warning: violations.filter((v) => v.severity === 'warning'),
  }

  const totalErrors = grouped.error.length
  const totalWarnings = grouped.warning.length

  if (totalErrors === 0 && totalWarnings === 0) {
    return null
  }

  return (
    <div css={containerStyles}>
      <div
        css={headerStyles}
        onClick={() => setExpanded((prev) => !prev)}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setExpanded((prev) => !prev)
          }
        }}
      >
        <ExpandArrow expanded={expanded} />
        <p css={titleStyles}>CONSTRAINT VIOLATIONS</p>
        {totalErrors > 0 && (
          <span css={badgeStyles(colors.ERROR_200)}>{totalErrors}</span>
        )}
        {totalWarnings > 0 && (
          <span css={badgeStyles(colors.WARNING_300)}>{totalWarnings}</span>
        )}
      </div>

      {expanded && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 12px;
          `}
        >
          {(['error', 'warning'] as const).map((severity) => {
            const items = grouped[severity]
            if (items.length === 0) {
              return null
            }
            const config = severityConfig[severity]

            return (
              <div key={severity} css={groupStyles}>
                <p css={groupHeaderStyles(config.borderColor)}>
                  {config.label} ({items.length})
                </p>
                {items.map((violation) => (
                  <div
                    key={violation.id}
                    css={violationRowStyles(config.bgColor, config.borderColor)}
                  >
                    <span css={violationTypeStyles}>
                      {violation.constraint_type?.name ??
                        violation.constraint_type?.code ??
                        'Unknown'}
                    </span>
                    <div
                      css={css`
                        flex: 1;
                      `}
                    >
                      <p css={violationMessageStyles}>{violation.message}</p>
                      {violation.resolution_suggestion && (
                        <p css={suggestionStyles}>
                          {violation.resolution_suggestion}
                        </p>
                      )}
                    </div>
                    {violation.affected_game_numbers &&
                      violation.affected_game_numbers.length > 0 && (
                        <span css={affectedGamesStyles}>
                          Games: {violation.affected_game_numbers.join(', ')}
                        </span>
                      )}
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ScheduleViolations
